import { getAuthToken } from './supabase';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Validate required environment variable
if (!API_BASE_URL) {
  throw new Error('Missing VITE_API_BASE_URL environment variable. Please check your .env file.');
}

// Helper function to make authenticated API requests
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(errorData.detail || `API Error: ${response.status}`);
  }

  return response.json();
}

// ============================================
// USERS & CREDITS API
// ============================================

export interface User {
  id: string;
  email: string;
  credit_balance: number;
  is_editor: boolean;
  email_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreditBalance {
  user_id: string;
  credit_balance: number;
}

export interface CreditHistoryItem {
  id: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  action: string;
  created_at: string;
}

export async function getCurrentUser(): Promise<User> {
  return apiRequest<User>('/users/me');
}

export async function getCreditBalance(): Promise<CreditBalance> {
  return apiRequest<CreditBalance>('/users/me/credits');
}

export async function getCreditHistory(limit = 50): Promise<CreditHistoryItem[]> {
  return apiRequest<CreditHistoryItem[]>(`/users/me/credit-history?limit=${limit}`);
}

// ============================================
// POSTS API
// ============================================

export interface Post {
  id: string;
  user_id: string;
  title: string;
  description: string;
  status: PostStatus;
  total_ai_generations: number;
  total_editor_submissions: number;
  created_at: string;
  updated_at: string;
}

export type PostStatus =
  | 'draft'
  | 'uploading_images'
  | 'ai_processing'
  | 'ai_completed'
  | 'ai_failed'
  | 'payment_pending'
  | 'payment_completed'
  | 'enhancement_processing'
  | 'enhancement_completed'
  | 'enhancement_failed'
  | 'completed';

export interface CreatePostRequest {
  title: string;
  description: string;
}

export interface PresignedUrlRequest {
  image_count: number;
}

export interface PresignedUrl {
  index: number;
  object_key: string;
  upload_url: string;
  fields?: Record<string, string>;
  expires_in: number;
}

export interface PresignedUrlResponse {
  post_id: string;
  presigned_urls: PresignedUrl[];
}

export interface ImageConfirmation {
  image_url: string;
  image_key: string;
  file_size_bytes: number;
  mime_type: string;
  width: number;
  height: number;
  display_order: number;
  is_primary: boolean;
}

export type ConfirmImagesRequest = ImageConfirmation[];

export interface ConfirmImagesResponse {
  message: string;
  post_id: string;
  images_count: number;
  status: string;
}

export interface DeletePostResponse {
  success: boolean;
  message?: string;
  post_id?: string;
}

export async function createPost(data: CreatePostRequest): Promise<Post> {
  return apiRequest<Post>('/posts/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getPresignedUrls(
  postId: string,
  imageCount: number
): Promise<PresignedUrlResponse> {
  const queryParam = new URLSearchParams({ image_count: String(imageCount) }).toString();
  return apiRequest<PresignedUrlResponse>(
    `/posts/${postId}/images/presigned-urls?${queryParam}`,
    { method: 'POST' }
  );
}

export async function confirmImageUploads(
  postId: string,
  images: ImageConfirmation[]
): Promise<ConfirmImagesResponse> {
  return apiRequest<ConfirmImagesResponse>(`/posts/${postId}/images/confirm`, {
    method: 'POST',
    body: JSON.stringify(images),
  });
}

export async function getPost(postId: string): Promise<Post> {
  return apiRequest<Post>(`/posts/${postId}`);
}

export async function listPosts(limit = 20, offset = 0): Promise<Post[]> {
  return apiRequest<Post[]>(`/posts/?limit=${limit}&offset=${offset}`);
}

export async function deletePost(postId: string): Promise<DeletePostResponse> {
  return apiRequest<DeletePostResponse>(`/posts/${postId}`, {
    method: 'DELETE',
  });
}

// ============================================
// POST STATUS & GENERATIONS API
// ============================================

export interface GenerationImage {
  url: string;
  key: string;
  width: number;
  height: number;
  file_size_bytes: number;
  task_id?: number;
  task_type?: string;
  temperature?: number;
  attempt?: number;
}

export interface Generation {
  id: string;
  post_id: string;
  post_image_id: string;
  iteration_number: number;
  prompt_text: string;
  images: {
    gemini_output: GenerationImage;
    watermarked: GenerationImage;
    enhanced?: GenerationImage;
    metadata?: Record<string, any>;
  };
  status: GenerationStatus;
  created_at: string;
  completed_at?: string;
}

export type GenerationStatus =
  | 'processing'
  | 'completed'
  | 'failed'
  | 'paid'
  | 'enhancement_processing'
  | 'enhancement_completed'
  | 'enhancement_failed';

export interface PostImage {
  id: string;
  post_id: string;
  image_url: string;
  image_key: string;
  width: number;
  height: number;
  display_order: number;
  is_primary: boolean;
  created_at: string;
}

export interface PostStatusResponse {
  post_id: string;
  title: string;
  status: PostStatus;
  suggested_action?: string;
  total_images: number;
  total_generations: number;
  generations: Generation[];
  generations_by_image: Record<string, Generation[]>;
  post_images: PostImage[];
  updated_at: string;
}

export async function getPostStatus(postId: string): Promise<PostStatusResponse> {
  return apiRequest<PostStatusResponse>(`/posts/${postId}/status`);
}

export async function getGeneration(generationId: string): Promise<Generation> {
  return apiRequest<Generation>(`/generations/${generationId}`);
}

export async function selectGeneration(generationId: string): Promise<{ success: boolean; selected_generation_id: string }> {
  return apiRequest<{ success: boolean; selected_generation_id: string }>(
    `/generations/${generationId}/select`,
    { method: 'POST' }
  );
}

export interface RefineRequest {
  post_image_id: string;
  prompt: string;
}

export interface RefineResponse {
  message: string;
  post_id: string;
  post_image_id: string;
  iteration_number: number;
  status: string;
}

export async function createRefinement(
  postId: string,
  data: RefineRequest
): Promise<RefineResponse> {
  return apiRequest<RefineResponse>(`/generations/posts/${postId}/refine`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getPostGenerations(postId: string): Promise<Generation[]> {
  return apiRequest<Generation[]>(`/generations/posts/${postId}/generations`);
}

// ============================================
// PAYMENTS API
// ============================================

export interface PurchaseCreditsRequest {
  pack_size: 10 | 50 | 100;
}

export interface PurchaseCreditsResponse {
  order_id: string;
  approval_url: string;
  amount: number;
  credits: number;
}

export interface CaptureCreditsResponse {
  success: boolean;
  credits_added: number;
  new_balance: number;
  transaction_id: string;
}

export interface PurchaseDownloadRequest {
  generation_id: string;
}

export interface PurchaseDownloadResponse {
  order_id: string;
  approval_url: string;
  amount: number;
  credits_reward: number;
}

export interface CaptureDownloadResponse {
  success: boolean;
  credits_added: number;
  new_balance: number;
  generation_id: string;
  status: string;
}

export interface DownloadUrlResponse {
  download_url: string;
  expires_at: string;
  file_size_bytes: number;
  width: number;
  height: number;
}

export async function purchaseCredits(packSize: 10 | 50 | 100): Promise<PurchaseCreditsResponse> {
  return apiRequest<PurchaseCreditsResponse>('/payments/credits/purchase', {
    method: 'POST',
    body: JSON.stringify({ pack_size: packSize }),
  });
}

export async function captureCreditsPurchase(orderId: string): Promise<CaptureCreditsResponse> {
  return apiRequest<CaptureCreditsResponse>(
    `/payments/credits/capture?order_id=${orderId}`,
    { method: 'POST' }
  );
}

export async function purchaseDownload(generationId: string): Promise<PurchaseDownloadResponse> {
  return apiRequest<PurchaseDownloadResponse>('/payments/generation/download', {
    method: 'POST',
    body: JSON.stringify({ generation_id: generationId }),
  });
}

export async function captureDownloadPurchase(orderId: string): Promise<CaptureDownloadResponse> {
  return apiRequest<CaptureDownloadResponse>(
    `/payments/generation/capture?order_id=${orderId}`,
    { method: 'POST' }
  );
}

export async function getDownloadUrl(generationId: string): Promise<DownloadUrlResponse> {
  return apiRequest<DownloadUrlResponse>(`/payments/generation/${generationId}/download`);
}

// ============================================
// HELPER FUNCTIONS
// ============================================

// Upload image directly to Backblaze B2
export async function uploadToB2(presignedData: PresignedUrl, file: File): Promise<void> {
  const response = await fetch(presignedData.upload_url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload to B2: ${response.status}`);
  }
}

// Get image dimensions from file
export async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

// Prepare image confirmation data
export async function prepareImageConfirmation(
  presignedUrl: PresignedUrl,
  file: File,
  index: number
): Promise<ImageConfirmation> {
  const dimensions = await getImageDimensions(file);
  const uploadUrl = new URL(presignedUrl.upload_url);
  const baseUrl = `${uploadUrl.origin}${uploadUrl.pathname}`;
  const normalizedBase = baseUrl.endsWith(presignedUrl.object_key)
    ? baseUrl
    : `${baseUrl.replace(/\/$/, '')}/${presignedUrl.object_key}`;

  return {
    image_url: normalizedBase,
    image_key: presignedUrl.object_key,
    file_size_bytes: file.size,
    mime_type: file.type,
    width: dimensions.width,
    height: dimensions.height,
    display_order: index,
    is_primary: index === 0,
  };
}

// ============================================
// AMBASSADOR API
// ============================================

export interface AmbassadorStatus {
  enabled: boolean;
}

export interface AmbassadorUser {
  id: string;
  email: string;
  name: string;
  is_ambassador: boolean;
  is_active: boolean;
}

export interface AmbassadorUserListResponse {
  users: AmbassadorUser[];
  total: number;
  page: number;
  limit: number;
}

export interface RedditAccount {
  id: string;
  username: string;
  karma: number;
  is_valid: boolean;
  last_validated_at: string;
}

export interface AmbassadorTask {
  task_id: string;
  reddit_post_id: string;
  image_url: string;
  username: string;
  password: string;
  generation_id: string;
}

export interface HeartbeatResponse {
  status: string;
  pending_tasks: AmbassadorTask[];
}

export interface AmbassadorLoginResponse {
  ambassador_id: string;
  name: string;
  email: string;
}

// Admin Functions
export async function getAmbassadorSystemStatus(): Promise<AmbassadorStatus> {
  return apiRequest<AmbassadorStatus>('/ambassadors/admin/status');
}

export async function toggleAmbassadorSystem(enabled: boolean): Promise<AmbassadorStatus> {
  return apiRequest<AmbassadorStatus>('/ambassadors/admin/toggle', {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}

export async function listAmbassadorUsers(page = 1, limit = 10): Promise<AmbassadorUserListResponse> {
  return apiRequest<AmbassadorUserListResponse>(`/ambassadors/admin/users?page=${page}&limit=${limit}`);
}

export async function toggleAmbassadorUser(userId: string): Promise<void> {
  return apiRequest<void>(`/ambassadors/admin/ambassadors/${userId}/toggle`, {
    method: 'POST',
  });
}

// Ambassador Functions
export async function ambassadorLogin(googleUid: string, email: string): Promise<AmbassadorLoginResponse> {
  return apiRequest<AmbassadorLoginResponse>('/ambassadors/login', {
    method: 'POST',
    body: JSON.stringify({ google_uid: googleUid, email }),
  });
}

export async function getAmbassadorAccounts(ambassadorId: string): Promise<RedditAccount[]> {
  return apiRequest<RedditAccount[]>(`/ambassadors/accounts?ambassador_id=${ambassadorId}`);
}

export async function addAmbassadorAccount(
  ambassadorId: string,
  username: string,
  password: string
): Promise<RedditAccount> {
  return apiRequest<RedditAccount>('/ambassadors/accounts', {
    method: 'POST',
    body: JSON.stringify({ ambassador_id: ambassadorId, username, password }),
  });
}

export async function sendAmbassadorHeartbeat(ambassadorId: string): Promise<HeartbeatResponse> {
  return apiRequest<HeartbeatResponse>('/ambassadors/heartbeat', {
    method: 'POST',
    body: JSON.stringify({ ambassador_id: ambassadorId }),
  });
}

export async function completeAmbassadorTask(
  taskId: string,
  status: 'posted' | 'failed',
  redditCommentId?: string,
  errorMessage?: string
): Promise<void> {
  return apiRequest<void>(`/ambassadors/tasks/${taskId}/complete`, {
    method: 'POST',
    body: JSON.stringify({
      status,
      reddit_comment_id: redditCommentId,
      error_message: errorMessage,
    }),
  });
}

export async function getRedditAuthToken(username: string, password: string): Promise<{ access_token: string }> {
  return apiRequest<{ access_token: string }>('/ambassadors/auth_token', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export async function deleteAmbassadorAccount(accountId: string, ambassadorId: string): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/ambassadors/accounts/${accountId}`, {
    method: 'DELETE',
    body: JSON.stringify({ ambassador_id: ambassadorId }),
  });
}


export interface AmbassadorPost {
  id: string;
  reddit_post_id: string;
  reddit_comment_id: string | null;
  status: string;
  image_url: string | null;
  created_at: string;
  username: string;
}

export interface AmbassadorDashboardStats {
  total_posts: number;
  total_karma: number;
  active_accounts: number;
  success_rate: number;
  total_tips: number;
}

export async function getAmbassadorPosts(ambassadorId: string, limit = 20, offset = 0): Promise<AmbassadorPost[]> {
  return apiRequest<AmbassadorPost[]>(`/ambassadors/posts?ambassador_id=${ambassadorId}&limit=${limit}&offset=${offset}`);
}

export async function getAmbassadorStats(ambassadorId: string): Promise<AmbassadorDashboardStats> {
  return apiRequest<AmbassadorDashboardStats>(`/ambassadors/stats?ambassador_id=${ambassadorId}`);
}

export interface TipRequest {
  amount: number;
  generation_id?: string;
  ambassador_id?: string;
}

export interface TipOrderResponse {
  order_id: string;
  approval_url: string;
  amount: number;
}

export async function createTipOrder(data: TipRequest): Promise<TipOrderResponse> {
  return apiRequest<TipOrderResponse>("/payments/tip/create", {
    method: "POST",
    body: JSON.stringify(data)
  });
}

export async function captureTipOrder(orderId: string): Promise<{ success: boolean; amount: number }> {
  return apiRequest<{ success: boolean; amount: number }>(`/payments/tip/capture?order_id=${orderId}`, {
    method: "POST"
  });
}

export interface PaymentConfig {
  min_tip_amount: number;
  currency: string;
}

export async function getPaymentConfig(): Promise<PaymentConfig> {
  return apiRequest<PaymentConfig>("/payments/config");
}

export async function manualSubmitPost(
  ambassadorId: string,
  redditPostId: string
): Promise<{ message: string; internal_post_id: string; reddit_post_id: string }> {
  return apiRequest<{ message: string; internal_post_id: string; reddit_post_id: string }>(
    '/ambassadors/manual_submit',
    {
      method: 'POST',
      body: JSON.stringify({ ambassador_id: ambassadorId, reddit_post_id: redditPostId }),
    }
  );
}

export interface AmbassadorPostDetail {
  id: string;
  reddit_post_id: string;
  reddit_comment_id: string | null;
  status: string;
  image_url: string | null;
  created_at: string;
  updated_at: string | null;
  username: string;
  error_message: string | null;
  generation_id: string | null;
  generation_url: string | null;
  total_downloads: number;
  last_download_at: string | null;
  total_tips: number;
  tip_count: number;
  last_tip_at: string | null;
}

export async function getAmbassadorPostDetail(
  postId: string,
  ambassadorId: string
): Promise<AmbassadorPostDetail> {
  return apiRequest<AmbassadorPostDetail>(
    `/ambassadors/posts/${postId}?ambassador_id=${ambassadorId}`
  );
}


// ============================================
// ADMIN API
// ============================================

export interface AdminStats {
  total_users: number;
  total_posts: number;
  total_generations: number;
  total_ambassadors: number;
  active_ambassadors: number;
  pending_ambassador_tasks: number;
}

export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  credit_balance: number;
  created_at: string;
  is_active: boolean;
  is_ambassador: boolean;
}

export interface AdminUserListResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminUserDetail {
  user: AdminUser;
  credit_history: CreditHistoryItem[];
  stats: {
    total_generations: number;
    total_posts: number;
  };
}

export interface AdminPost {
  id: string;
  title: string | null;
  user_email: string;
  status: string;
  created_at: string;
  generations_count: number;
}

export interface AdminPostListResponse {
  posts: AdminPost[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminGeneration {
  id: string;
  post_id: string;
  user_email: string;
  prompt: string;
  status: string;
  created_at: string;
  image_url: string | null;
}

export interface AdminGenerationListResponse {
  generations: AdminGeneration[];
  total: number;
  page: number;
  limit: number;
}

export interface AmbassadorStats {
  total_ambassadors: number;
  active_ambassadors: number;
  total_tasks_completed: number;
  recent_activity: {
    date: string;
    user: string;
    action: string;
  }[];
}

export async function getAdminStats(): Promise<AdminStats> {
  return apiRequest<AdminStats>('/admin/stats');
}

export async function getAdminUsers(page = 1, limit = 20): Promise<AdminUserListResponse> {
  return apiRequest<AdminUserListResponse>(`/admin/users?page=${page}&limit=${limit}`);
}

export async function getAdminUserDetails(userId: string): Promise<AdminUserDetail> {
  return apiRequest<AdminUserDetail>(`/admin/users/${userId}`);
}

export async function getAdminUserGenerations(userId: string, page = 1, limit = 20): Promise<AdminGenerationListResponse> {
  return apiRequest<AdminGenerationListResponse>(`/admin/users/${userId}/generations?page=${page}&limit=${limit}`);
}

export async function getAdminPosts(page = 1, limit = 20): Promise<AdminPostListResponse> {
  return apiRequest<AdminPostListResponse>(`/admin/posts?page=${page}&limit=${limit}`);
}

export async function getAdminGenerations(page = 1, limit = 20): Promise<AdminGenerationListResponse> {
  return apiRequest<AdminGenerationListResponse>(`/admin/generations?page=${page}&limit=${limit}`);
}

export interface PaymentStats {
  total_revenue: number;
  total_transactions: number;
  revenue_by_type: Record<string, number>;
  recent_transactions: {
    date: string;
    amount: number;
    type: string;
    user: string;
  }[];
}

// ============================================
// VIDEO CREATOR API
// ============================================

export interface VideoAnnotation {
  image_index: number;
  description?: string;
  desired_action?: string;
}

export interface VideoClip {
  image_index: number;
  image_url?: string;
  script?: string;
  veo_prompt: string;
  action_description: string;
  duration: number;
  transition: string;
  mood: string;
}

export interface VideoStory {
  title: string;
  clips: VideoClip[];
}

export interface VideoUploadResponse {
  upload_session_id: string;
  file_count: number;
  message: string;
}

export async function uploadVideoFiles(files: File[]): Promise<VideoUploadResponse> {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  return apiRequest<VideoUploadResponse>('/video-creator/upload', {
    method: 'POST',
    body: formData,
    // Content-Type header is automatically set by browser for FormData
    headers: {},
  });
}

export async function generateVideoStory(
  uploadSessionId: string,
  theme: string,
  annotations: VideoAnnotation[] = []
): Promise<VideoStory> {
  return apiRequest<VideoStory>('/video-creator/story', {
    method: 'POST',
    body: JSON.stringify({
      upload_session_id: uploadSessionId,
      theme,
      annotations,
    }),
  });
}

export async function generateVideoPreview(story: VideoStory): Promise<{ message: string; status: string }> {
  return apiRequest<{ message: string; status: string }>('/video-creator/preview', {
    method: 'POST',
    body: JSON.stringify(story),
  });
}

export async function generateFullVideo(story: VideoStory): Promise<{ message: string; status: string }> {
  return apiRequest<{ message: string; status: string }>('/video-creator/generate', {
    method: 'POST',
    body: JSON.stringify(story),
  });
}

export async function finalizeVideo(videoUrl: string, audioUrl: string): Promise<{ video_url: string; status: string }> {
  return apiRequest<{ video_url: string; status: string }>(
    `/video-creator/finalize?video_url=${encodeURIComponent(videoUrl)}&audio_url=${encodeURIComponent(audioUrl)}`,
    { method: 'POST' }
  );
}

export async function getAdminAmbassadorStats(): Promise<AmbassadorStats> {
  return apiRequest<AmbassadorStats>('/admin/analytics/ambassadors');
}

export async function getPaymentAnalytics(): Promise<PaymentStats> {
  return apiRequest<PaymentStats>('/admin/analytics/payments');
}

export interface PollerSettings {
  schedule_interval_minutes: number;
  posts_per_run: number;
  min_tip_amount: number;
  max_comments: number;
}

export async function getPollerSettings(): Promise<PollerSettings> {
  return apiRequest<PollerSettings>('/admin/settings/poller');
}

export async function updatePollerSettings(settings: PollerSettings): Promise<PollerSettings> {
  return apiRequest<PollerSettings>('/admin/settings/poller', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}
